import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks';
import { loginUser, clearError } from '../../store/slices/authSlice';
import { useAuth } from '../../hooks/useAuth';
import { Input } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Modal';
import { config } from '../../config/env';
import LogoImage from '../../assets/logo.png';
import toast from 'react-hot-toast';
import { Ico } from '../../components/ui/Icon';

const schema = z.object({
  email:    z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});
type FormData = z.infer<typeof schema>;

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated, isAdmin } = useAuth();
  const [logoFailed, setLogoFailed] = React.useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  // FIX: only navigate when authenticated AND no error is set.
  // Without !error the page refreshes on failed login because the useEffect
  // fires mid-dispatch and sees a stale token before the error lands.
  useEffect(() => {
    if (isAuthenticated && !error) {
      navigate(isAdmin ? '/admin' : '/dashboard', { replace: true });
    }
  }, [isAuthenticated, isAdmin, error, navigate]);

  // Clear redux error when unmounting so it never bleeds into other pages
  useEffect(() => { return () => { dispatch(clearError()); }; }, [dispatch]);

  const onSubmit = async (data: FormData) => {
    dispatch(clearError());
    const result = await dispatch(loginUser(data));
    if (loginUser.fulfilled.match(result)) toast.success('Welcome back! 👋');
    // on rejected: error is set in Redux → shown by Alert below, no navigate
  };

  return (
    <div className="min-h-screen bg-[#060606] flex items-start sm:items-center justify-center p-4 py-10">
      <div className="fixed top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center mb-8">
          <div className="mb-4">
            {!logoFailed ? (
              <img src={LogoImage} alt={config.appName} className="w-14 h-14 object-contain" onError={() => setLogoFailed(true)} />
            ) : (
              <Ico name="chart" className="w-14 h-14 text-primary" />
            )}
          </div>
          <h1 className="font-display text-3xl font-bold text-white tracking-tight">{config.appName}</h1>
          <p className="text-gray-500 text-sm mt-1">Sign in to your trading account</p>
        </div>

        <div className="bg-gray-900/80 backdrop-blur-md rounded-2xl border border-gray-800/60 p-8 shadow-2xl">
          {error && (
            <div className="mb-5">
              <Alert type="error" message={typeof error === 'string' ? error : 'Login failed. Please try again.'} />
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
            <Input
              label="Email Address" type="email" placeholder="you@example.com"
              prefix={<Ico name="mail" className="w-4 h-4" />}
              error={errors.email?.message}
              autoComplete="email"
              {...register('email')}
            />
            <div>
              <Input
                label="Password" type="password" placeholder="••••••••"
                prefix={<Ico name="lock" className="w-4 h-4" />}
                error={errors.password?.message}
                autoComplete="current-password"
                {...register('password')}
              />
              <div className="flex justify-end mt-1.5">
                <Link to="/forgot-password" className="text-xs text-gray-500 hover:text-primary transition-colors">
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button type="submit" fullWidth loading={loading} size="lg">Sign In</Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary hover:underline font-semibold">Create one</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
